import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Circle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { useSidebar } from '../../contexts/SidebarContext';
import { useTheme } from '../../contexts/ThemeContext';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import {
  PHOTO_GROUPS,
  getMissingClinicalPhotoKeys,
  getPhotoGroupSections,
  getPhotoLabelByKey,
  hasAllRequiredClinicalPhotos,
} from '../../case-dentist/scanUploadConfig';
import { canModifyCaseFilesDuringSubmission } from '../../lib/casePrescriptionEditPolicy';
import { uploadCaseStlScans, uploadCaseClinicalPhotos } from '../../services/caseStlPhotoUploads';
import { refreshCaseFileMilestones, getAllCaseFilesList } from '../../lib/caseMilestoneFileSync';
import {
  fetchSignedUrlsByFileId,
  mapSignedUrlsToPhotoKeys,
  revokePreviewBlobUrls,
} from '../../lib/caseFileSignedUrls';
import {
  getSubmissionPhaseId,
  runDentistCaseSubmission,
} from '../../services/caseDentistSubmissionService';
import { formatMissingPhotosSubmissionError } from '../../lib/dentistCaseSubmissionValidation';
import {
  getAssistantResumeStep,
  isSubmissionPhaseSubmitted,
} from '../../lib/dentistSubmissionWizard';
import caseServicesService from '../../services/caseServicesService';
import { toast } from 'sonner';
import SubmissionSuccessOverlay from './SubmissionSuccessOverlay';
import StlArchUploadCard from './StlArchUploadCard';
import PhotoSlotUploadCard from './PhotoSlotUploadCard';
import AssistantPrescriptionForm from './AssistantPrescriptionForm';
import ServiceSelectionPanel from '../service-package-integration/components/ServiceSelectionPanel';

const STEPS = [
  { id: 1, label: 'Scans STL' },
  { id: 2, label: 'Photos' },
  { id: 3, label: 'Prescription' },
  { id: 4, label: 'Pack et services' },
  { id: 5, label: 'Validation' },
];

const CaseDentistAssistantPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { setTheme } = useTheme();
  const [currentUser, setCurrentUser] = useState(null);
  const [isSubmittingCase, setIsSubmittingCase] = useState(false);
  const [showSubmissionSuccess, setShowSubmissionSuccess] = useState(false);
  const [caseRow, setCaseRow] = useState(null);
  const [patientId, setPatientId] = useState(null);
  const [caseNumber, setCaseNumber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [step, setStep] = useState(1);
  const [upperFile, setUpperFile] = useState(null);
  const [lowerFile, setLowerFile] = useState(null);
  const [photoFiles, setPhotoFiles] = useState({});
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState({});
  const [uploadedPhotoPreviewUrls, setUploadedPhotoPreviewUrls] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const stlUploadLockRef = useRef(false);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [phaseStatus, setPhaseStatus] = useState(null);
  const [dentistCountryId, setDentistCountryId] = useState(null);
  const [commercialPacks, setCommercialPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [packSaving, setPackSaving] = useState(false);
  const [dentistCurrencyCode, setDentistCurrencyCode] = useState('EUR');
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesSaving, setServicesSaving] = useState(false);
  const [viewerIsAdmin, setViewerIsAdmin] = useState(false);
  const [viewerRoleTypes, setViewerRoleTypes] = useState([]);

  useEffect(() => {
    const urls = {};
    Object.entries(photoFiles || {}).forEach(([key, f]) => {
      if (f) urls[key] = URL.createObjectURL(f);
    });
    setPhotoPreviewUrls(urls);
    return () => {
      Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [photoFiles]);

  const loadContext = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (!uid) {
        navigate('/login');
        return;
      }
      setCurrentUser({ id: uid });

      const { data: roleAssignments } = await supabase
        .from('user_role_assignments')
        .select('user_roles(role_type)')
        .eq('user_id', uid)
        .eq('is_active', true);
      const types = (roleAssignments || [])
        .map((r) => r.user_roles?.role_type)
        .filter(Boolean);
      const isAdmin = types.includes('administrator');
      setViewerIsAdmin(isAdmin || types.includes('agent_administratif'));
      setViewerRoleTypes(types);
      if (!isAdmin && types.includes('dentist_client') && !localStorage.getItem('appTheme')) {
        setTheme('neutral');
      }
      const { data: c, error: ce } = await supabase
        .from('cases')
        .select(
          `
        id,
        case_number,
        user_profile_id,
        patient_id,
        status,
        package,
        arch_to_treat,
        treatment_type,
        casePackage:commercial_packages!cases_package_fkey(id, package_name, package_code, tier)
      `,
        )
        .eq('id', caseId)
        .single();
      if (ce || !c) {
        setError('Cas introuvable');
        return;
      }
      const canAccessAsStaff =
        isAdmin || types.includes('agent_administratif');
      if (!canAccessAsStaff && c.user_profile_id !== uid) {
        setForbidden(true);
        return;
      }
      setCaseRow(c);
      setCaseNumber(c.case_number);
      setPatientId(c.patient_id);

      const { data: dentistProf } = await supabase
        .from('user_profiles')
        .select('country_id')
        .eq('id', c.user_profile_id)
        .maybeSingle();
      setDentistCountryId(dentistProf?.country_id ?? null);

      if (dentistProf?.country_id) {
        const { data: countryRow } = await supabase
          .from('countries')
          .select('currency_code')
          .eq('id', dentistProf.country_id)
          .maybeSingle();
        setDentistCurrencyCode(countryRow?.currency_code || 'EUR');
      } else {
        setDentistCurrencyCode('EUR');
      }

      try {
        const ids = await caseServicesService.getSelectedServiceIds(caseId);
        setSelectedServiceIds(ids);
      } catch (svcErr) {
        console.warn('case_services load', svcErr);
        setSelectedServiceIds([]);
      }

      const list = await refreshCaseFileMilestones(caseId);
      setFiles(list);

      const { data: ph1 } = await supabase
        .from('case_timeline_phases')
        .select('status')
        .eq('case_id', caseId)
        .eq('group', '1')
        .eq('phase_order', 1)
        .maybeSingle();
      const phStatus = ph1?.status || null;
      setPhaseStatus(phStatus);

      if (!isSubmissionPhaseSubmitted(phStatus)) {
        setStep(
          getAssistantResumeStep({
            files: list,
            caseRow: c,
          }),
        );
      }
    } catch (e) {
      console.error(e);
      setError(e.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (caseId) loadContext();
  }, [caseId]);

  useEffect(() => {
    if (step === 5 && caseId) {
      getAllCaseFilesList(caseId).then(setFiles);
    }
  }, [step, caseId]);

  const uploadedStlScans = useMemo(
    () => ({
      upper: files.find(
        (f) => f.category === 'scans' && String(f.filePath || '').includes('-upper.stl'),
      ),
      lower: files.find(
        (f) => f.category === 'scans' && String(f.filePath || '').includes('-lower.stl'),
      ),
    }),
    [files],
  );

  const hasScans = useMemo(
    () => Boolean(uploadedStlScans.upper || uploadedStlScans.lower),
    [uploadedStlScans],
  );
  const uploadedPhotosByKey = useMemo(() => {
    const map = {};
    for (const f of files) {
      if (f.category !== 'photos' || !f.photoKey) continue;
      map[f.photoKey] = f;
    }
    return map;
  }, [files]);

  useEffect(() => {
    const photos = Object.values(uploadedPhotosByKey);
    if (!photos.length) {
      setUploadedPhotoPreviewUrls({});
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const signedById = await fetchSignedUrlsByFileId(photos);
      if (cancelled) {
        revokePreviewBlobUrls(signedById);
        return;
      }
      setUploadedPhotoPreviewUrls((prev) => {
        revokePreviewBlobUrls(prev);
        return mapSignedUrlsToPhotoKeys(uploadedPhotosByKey, signedById);
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [uploadedPhotosByKey]);

  const submissionTimeline = useMemo(
    () => [{ group: '1', phase_order: 1, status: phaseStatus }],
    [phaseStatus],
  );

  const canModifyFiles = useMemo(
    () =>
      canModifyCaseFilesDuringSubmission({
        userRoleTypes: viewerRoleTypes,
        caseData: caseRow,
        timelinePhaseItems: submissionTimeline,
      }),
    [caseRow, submissionTimeline, viewerRoleTypes],
  );

  const hasAllRequiredPhotos = useMemo(
    () => hasAllRequiredClinicalPhotos(uploadedPhotosByKey),
    [uploadedPhotosByKey],
  );

  const missingPhotoKeys = useMemo(
    () => getMissingClinicalPhotoKeys(uploadedPhotosByKey),
    [uploadedPhotosByKey],
  );

  const canGoStep3 = hasAllRequiredPhotos;
  const hasPackage = !!caseRow?.package;
  const hasSelectedServices = selectedServiceIds.length > 0;
  const canEditOrderOptions =
    hasAllRequiredPhotos &&
    !!phaseStatus &&
    !['submitted', 'completed'].includes(phaseStatus) &&
    caseRow?.status !== 'submitted';
  const canEditPackage = canEditOrderOptions;
  const canEditServices = canEditOrderOptions;

  const canSubmit =
    hasAllRequiredPhotos &&
    hasPackage &&
    !!phaseStatus &&
    !['submitted', 'completed'].includes(phaseStatus) &&
    caseRow?.status !== 'submitted';

  const refreshCaseRow = useCallback(async () => {
    if (!caseId) return;
    const { data: c, error } = await supabase
      .from('cases')
      .select(
        `
        id,
        case_number,
        user_profile_id,
        patient_id,
        status,
        package,
        casePackage:commercial_packages!cases_package_fkey(id, package_name, package_code, tier)
      `,
      )
      .eq('id', caseId)
      .single();
    if (!error && c) setCaseRow(c);
  }, [caseId]);

  const handlePackageAssigned = useCallback(
    async (packageId) => {
      try {
        const now = new Date().toISOString();
        const { error } = await supabase.from('cases').update({ package: packageId, updated_at: now }).eq('id', caseId);
        if (error) throw error;
        toast.success('Pack enregistré sur le dossier');
        await refreshCaseRow();
      } catch (e) {
        console.error(e);
        toast.error(e?.message || 'Impossible d’enregistrer le pack');
      }
    },
    [caseId, refreshCaseRow],
  );

  const formatPackPrice = useCallback((price, currencyCode = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode || 'EUR',
    }).format(Number(price) || 0);
  }, []);

  useEffect(() => {
    if (step !== 4 || !dentistCountryId) {
      if (step !== 4) setCommercialPacks([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setPacksLoading(true);
      try {
        const { data, error } = await supabase
          .from('commercial_packages')
          .select('id, package_name, package_code, tier, final_price, countries ( currency_code )')
          .eq('country_id', dentistCountryId)
          .eq('status', 'active')
          .order('tier', { ascending: true })
          .order('final_price', { ascending: true });
        if (cancelled) return;
        if (error) throw error;
        setCommercialPacks(data || []);
      } catch (e) {
        console.error(e);
        toast.error(e?.message || 'Impossible de charger les packs');
        setCommercialPacks([]);
      } finally {
        if (!cancelled) setPacksLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, dentistCountryId]);

  useEffect(() => {
    if (!dentistCountryId || step !== 4) {
      if (step !== 4) setAvailableServices([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setServicesLoading(true);
      try {
        const list = await caseServicesService.getAvailableServicesForCountry(
          dentistCountryId,
          dentistCurrencyCode,
        );
        if (!cancelled) setAvailableServices(list);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          toast.error(e?.message || 'Impossible de charger les services');
          setAvailableServices([]);
        }
      } finally {
        if (!cancelled) setServicesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, dentistCountryId, dentistCurrencyCode]);

  const persistCaseServices = useCallback(
    async (serviceIds) => {
      if (!caseId || !dentistCountryId || !canEditServices) return;
      setServicesSaving(true);
      try {
        await caseServicesService.replaceForCase(
          caseId,
          dentistCountryId,
          serviceIds,
          dentistCurrencyCode,
        );
        setSelectedServiceIds(serviceIds);
      } catch (e) {
        console.error(e);
        toast.error(e?.message || 'Impossible d’enregistrer les services');
        const ids = await caseServicesService.getSelectedServiceIds(caseId);
        setSelectedServiceIds(ids);
      } finally {
        setServicesSaving(false);
      }
    },
    [caseId, dentistCountryId, dentistCurrencyCode, canEditServices],
  );

  const handleServiceToggle = useCallback(
    (serviceId) => {
      if (!canEditServices || servicesSaving) return;
      const next = selectedServiceIds.includes(serviceId)
        ? selectedServiceIds.filter((id) => id !== serviceId)
        : [...selectedServiceIds, serviceId];
      setSelectedServiceIds(next);
      persistCaseServices(next);
    },
    [canEditServices, servicesSaving, selectedServiceIds, persistCaseServices],
  );

  const packSelectOptions = useMemo(
    () =>
      (commercialPacks || []).map((pkg) => ({
        value: pkg.id,
        label: `${pkg.package_name} (${pkg.package_code}) · ${pkg.tier} — ${formatPackPrice(
          pkg.final_price,
          pkg.countries?.currency_code,
        )}`,
      })),
    [commercialPacks, formatPackPrice],
  );

  const handlePackSelect = useCallback(
    async (packageId) => {
      if (!packageId || packageId === caseRow?.package) return;
      setPackSaving(true);
      try {
        await handlePackageAssigned(packageId);
      } finally {
        setPackSaving(false);
      }
    },
    [caseRow?.package, handlePackageAssigned],
  );

  const handleStlUpload = async () => {
    if (stlUploadLockRef.current || isUploading) return;
    if (!caseId || !patientId || !currentUser?.id) return;
    if (!upperFile && !lowerFile) {
      setError('Sélectionnez au moins un fichier STL (sup. ou inf.)');
      return;
    }
    stlUploadLockRef.current = true;
    setIsUploading(true);
    setError(null);
    try {
      await uploadCaseStlScans({
        upperFile,
        lowerFile,
        caseId,
        patientId,
        currentUserId: currentUser.id,
      });
      const list = await refreshCaseFileMilestones(caseId);
      setFiles(list);
      setUpperFile(null);
      setLowerFile(null);
      toast.success('Scans enregistrés — traitement 3D en cours');
    } catch (e) {
      setError(e.message || 'Échec du téléversement');
    } finally {
      stlUploadLockRef.current = false;
      setIsUploading(false);
    }
  };

  const handlePhotoFileSelect = (event, key) => {
    const file = event.target.files[0];
    if (!file) return;
    setPhotoFiles((prev) => ({ ...prev, [key]: file }));
  };

  const removePhotoFile = (key) => {
    setPhotoFiles((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleRemovePhoto = async (key) => {
    if (!canModifyFiles) return;
    if (photoFiles[key]) {
      removePhotoFile(key);
      return;
    }
    const record = uploadedPhotosByKey[key];
    if (!record?.id || !caseId) return;
    try {
      const { error } = await supabase
        .from('case_photos')
        .update({ is_active: false })
        .eq('id', record.id);
      if (error) throw error;
      const list = await refreshCaseFileMilestones(caseId);
      setFiles(list);
      setUploadedPhotoPreviewUrls((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      toast.success('Photo supprimée');
    } catch (e) {
      toast.error(e.message || 'Impossible de supprimer la photo');
    }
  };

  const handlePhotosUpload = async () => {
    if (!canModifyFiles) return;
    if (!caseId || !patientId || !currentUser?.id) return;
    const entries = Object.entries(photoFiles || {}).filter(([, f]) => !!f);
    if (!entries.length) {
      setError('Sélectionnez au moins une photo');
      return;
    }
    setIsUploading(true);
    setError(null);
    try {
      await uploadCaseClinicalPhotos({
        photoFiles,
        caseId,
        patientId,
        currentUserId: currentUser.id,
      });
      const keysUploaded = Object.keys(photoFiles).filter((k) => photoFiles[k]);
      const list = await refreshCaseFileMilestones(caseId);
      setFiles(list);

      const byKey = {};
      for (const key of keysUploaded) {
        const rec = list.find((f) => f.category === 'photos' && f.photoKey === key);
        if (rec) byKey[key] = rec;
      }
      const signedById = await fetchSignedUrlsByFileId(Object.values(byKey));
      setUploadedPhotoPreviewUrls((prev) => {
        const next = { ...prev, ...mapSignedUrlsToPhotoKeys(byKey, signedById) };
        Object.keys(byKey).forEach((k) => {
          const old = prev[k];
          if (old?.startsWith?.('blob:') && old !== next[k]) URL.revokeObjectURL(old);
        });
        return next;
      });
      setPhotoFiles({});
      toast.success('Photos téléversées');
    } catch (e) {
      setError(e.message || 'Échec du téléversement des photos');
    } finally {
      setIsUploading(false);
    }
  };

  const submitSuccessRedirect = viewerIsAdmin
    ? `/case-detail/${caseId}`
    : '/dentiste';

  const goAfterSubmitSuccess = useCallback(
    (path) => {
      setShowSubmissionSuccess(false);
      navigate(path || submitSuccessRedirect, { replace: true });
    },
    [navigate, submitSuccessRedirect],
  );

  const handleSubmit = async () => {
    if (!caseId || isSubmittingCase) return;
    if (!canSubmit) {
      if (!hasAllRequiredPhotos) {
        toast.error(
          formatMissingPhotosSubmissionError(
            missingPhotoKeys,
            missingPhotoKeys.map((k) => getPhotoLabelByKey(k) || k),
          ),
        );
      } else if (!hasPackage) {
        toast.error('Sélectionnez un pack commercial avant d’envoyer la commande.');
      }
      return;
    }
    setIsSubmittingCase(true);
    try {
      const phaseId = await getSubmissionPhaseId(caseId);
      await runDentistCaseSubmission({
        caseId,
        caseNumber,
        phaseId,
        requirePackage: true,
      });
      setShowSubmissionSuccess(true);
    } catch (e) {
      toast.error('Erreur lors de la soumission : ' + (e?.message || e));
    } finally {
      setIsSubmittingCase(false);
    }
  };

  if (forbidden) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <p className="text-destructive">Accès refusé à ce dossier.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Chargement de l’assistant…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main
        className={`main-content min-w-0 ${isCollapsed ? 'sidebar-collapsed' : ''}`}
      >
        <Breadcrumb
          items={
            viewerIsAdmin
              ? [
                  { label: 'Commandes', path: '/cases-list', icon: 'Briefcase' },
                  { label: 'Envoi guidé (dentiste)', path: '#', icon: 'Send' },
                ]
              : [
                  { label: 'Espace dentiste', path: '/dentiste', icon: 'LayoutGrid' },
                  { label: 'Assistant envoi', path: '#', icon: 'Send' },
                ]
          }
        />
        <div className="w-full min-w-0 max-w-4xl mx-auto px-4 md:px-6 pb-12 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                {viewerIsAdmin ? 'Envoi guidé — commande dentiste' : 'Envoi guidé'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Cas {caseNumber || caseId} — scans, photos, prescription, choix du pack, validation.
              </p>
            </div>
            {isSubmissionPhaseSubmitted(phaseStatus) ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/case-detail/${caseId}`)}
              >
                Fiche complète
              </Button>
            ) : null}
          </div>

          <ol
            className="flex flex-wrap gap-2 sm:gap-2.5 items-stretch min-w-0"
            aria-label="Étapes"
          >
            {STEPS.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => s.id < step && setStep(s.id)}
                  className={`min-h-10 w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border shadow-sm transition-colors ${
                    step === s.id
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted/70'
                  }`}
                >
                  <span className="tabular-nums text-xs opacity-90">
                    {s.id}/{STEPS.length}
                  </span>
                  <span>{s.label}</span>
                </button>
              </li>
            ))}
          </ol>

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-destructive mt-0.5 shrink-0" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <p className="text-sm text-foreground">
                Les scans STL (arcades sup. / inf.) sont facultatifs. Ajoutez-les si vous les avez, puis passez aux
                photos.
              </p>
              <StlArchUploadCard
                variant="upper"
                inputId="stl-upper"
                pendingFile={upperFile}
                uploadedRecord={uploadedStlScans.upper}
                isUploading={isUploading && Boolean(upperFile)}
                disabled={isUploading}
                onFileChange={(e) => setUpperFile(e.target.files?.[0] || null)}
              />
              <StlArchUploadCard
                variant="lower"
                inputId="stl-lower"
                pendingFile={lowerFile}
                uploadedRecord={uploadedStlScans.lower}
                isUploading={isUploading && Boolean(lowerFile)}
                disabled={isUploading}
                onFileChange={(e) => setLowerFile(e.target.files?.[0] || null)}
              />
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="button"
                  onClick={handleStlUpload}
                  disabled={isUploading || (!upperFile && !lowerFile)}
                  loading={isUploading}
                  fullWidth
                >
                  {isUploading ? 'Téléversement…' : 'Téléverser les scans'}
                </Button>
                <Button
                  type="button"
                  variant="default"
                  onClick={() => setStep(2)}
                  disabled={isUploading}
                  fullWidth
                >
                  Continuer vers les photos
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 min-w-0">
              <p className="text-sm text-foreground">Ajoutez les 10 photos cliniques requises.</p>
              {!canModifyFiles && (
                <p className="text-sm text-muted-foreground rounded-lg border border-border bg-muted/30 px-3 py-2">
                  Conception soumise — les photos ne peuvent plus être modifiées.
                </p>
              )}

              <div className="space-y-10 min-w-0">
                {PHOTO_GROUPS.map((group) => (
                  <section key={group.id} className="space-y-4 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${group.color}`} />
                      <h4 className="font-semibold text-foreground text-sm">{group.title}</h4>
                    </div>
                    {getPhotoGroupSections(group).map((section, sIdx) => (
                      <div key={`${group.id}-sec-${sIdx}`} className="space-y-3 min-w-0">
                        {section.label && section.title && (
                          <p className="text-xs font-semibold text-muted-foreground">
                            {section.label} — {section.title}
                          </p>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
                          {section.items.map((item) => (
                            <PhotoSlotUploadCard
                              key={item.key}
                              item={item}
                              inputId={`p-${item.key}`}
                              pendingFile={photoFiles[item.key] || null}
                              previewUrl={photoPreviewUrls[item.key] || null}
                              uploadedRecord={uploadedPhotosByKey[item.key] || null}
                              uploadedPreviewUrl={uploadedPhotoPreviewUrls[item.key] || null}
                              isUploading={isUploading}
                              disabled={isUploading || !canModifyFiles}
                              onFileChange={(e) => handlePhotoFileSelect(e, item.key)}
                              onRemove={() => handleRemovePhoto(item.key)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </section>
                ))}
              </div>

              <div className="sticky bottom-0 z-10 -mx-4 px-4 py-4 md:-mx-6 md:px-6 border-t border-border bg-background/95 backdrop-blur-sm space-y-3">
                <div className="flex flex-col sm:flex-row gap-3 max-w-4xl mx-auto w-full">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    fullWidth
                    className="sm:flex-1"
                  >
                    Retour
                  </Button>
                  <Button
                    type="button"
                    onClick={handlePhotosUpload}
                    disabled={
                      !canModifyFiles ||
                      isUploading ||
                      !Object.values(photoFiles || {}).some(Boolean)
                    }
                    fullWidth
                    className="sm:flex-1"
                  >
                    {isUploading ? 'Téléversement…' : 'Téléverser les photos'}
                  </Button>
                  <Button
                    type="button"
                    onClick={async () => {
                      const list = await getAllCaseFilesList(caseId);
                      setFiles(list);
                      setStep(3);
                    }}
                    disabled={!canGoStep3}
                    fullWidth
                    className="sm:flex-1"
                  >
                    Continuer vers la prescription
                  </Button>
                </div>
                {canModifyFiles && !canGoStep3 && (
                  <p className="text-xs text-muted-foreground text-center max-w-4xl mx-auto">
                    {missingPhotoKeys.length} photo{missingPhotoKeys.length > 1 ? 's' : ''} sur 10
                    manquante{missingPhotoKeys.length > 1 ? 's' : ''} — téléversez chaque emplacement avant de
                    continuer.
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 3 && caseId && (
            <AssistantPrescriptionForm
              caseId={caseId}
              onBack={() => setStep(2)}
              onAfterSaveContinue={() => {
                if (!hasAllRequiredPhotos) {
                  toast.error(
                    formatMissingPhotosSubmissionError(
                      missingPhotoKeys,
                      missingPhotoKeys.map((k) => getPhotoLabelByKey(k) || k),
                    ),
                  );
                  setStep(2);
                  return;
                }
                setStep(4);
              }}
            />
          )}

          {step === 4 && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-foreground">Pack et services</h2>
              <p className="text-sm text-muted-foreground">
                Le pack commercial est obligatoire. Les services complémentaires sont optionnels (facturés sur la
                première facture de production si sélectionnés).
              </p>
              {!dentistCountryId && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                  Un pays doit être renseigné sur votre profil dentiste pour afficher les packs disponibles. Sans pays
                  assigné, la sélection de pack n’est pas possible.
                </div>
              )}
              <div className="rounded-xl border border-border/80 bg-card p-4 sm:p-5 space-y-4">
                <Select
                  label="Pack pour ce dossier"
                  required
                  placeholder={
                    packsLoading ? 'Chargement des packs…' : 'Sélectionnez un pack commercial…'
                  }
                  options={packSelectOptions}
                  value={caseRow?.package ?? ''}
                  onChange={(id) => handlePackSelect(id)}
                  disabled={
                    !canEditPackage ||
                    !dentistCountryId ||
                    packsLoading ||
                    packSaving ||
                    packSelectOptions.length === 0
                  }
                  loading={packsLoading || packSaving}
                  searchable={packSelectOptions.length > 6}
                  description={
                    dentistCountryId && !packsLoading && packSelectOptions.length === 0
                      ? 'Aucun pack actif pour votre pays. Contactez le support.'
                      : hasPackage
                        ? 'Le pack choisi est enregistré sur ce dossier. Vous pouvez le modifier tant que la conception n’est pas envoyée.'
                        : undefined
                  }
                />
              </div>

              {dentistCountryId && (
                <div className="min-w-0">
                  {servicesLoading ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">Chargement des services…</p>
                  ) : availableServices.length === 0 ? (
                    <p className="text-sm text-destructive rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
                      Aucun service actif pour votre pays. Contactez le support.
                    </p>
                  ) : (
                    <ServiceSelectionPanel
                      title="Services pour cette commande (optionnel)"
                      services={availableServices}
                      selectedServices={selectedServiceIds}
                      onServiceToggle={handleServiceToggle}
                      currencyCode={dentistCurrencyCode}
                      disabled={!canEditServices || servicesSaving || !hasPackage}
                      compact
                    />
                  )}
                  {!hasPackage && dentistCountryId && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Sélectionnez d’abord un pack pour activer le choix des services.
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Button type="button" variant="outline" onClick={() => setStep(3)} fullWidth>
                  Retour
                </Button>
                <Button
                  type="button"
                  onClick={() => setStep(5)}
                  disabled={
                    !hasPackage ||
                    !dentistCountryId ||
                    !hasAllRequiredPhotos ||
                    servicesSaving
                  }
                  fullWidth
                >
                  Continuer vers la validation
                </Button>
              </div>
              {!dentistCountryId && (
                <p className="text-xs text-muted-foreground">
                  Complétez votre pays dans la fiche profil ou contactez le support pour débloquer cette étape.
                </p>
              )}
              {hasPackage && dentistCountryId && !hasAllRequiredPhotos && (
                <p className="text-xs text-destructive">
                  {missingPhotoKeys.length} photo{missingPhotoKeys.length > 1 ? 's' : ''} obligatoire
                  {missingPhotoKeys.length > 1 ? 's' : ''} manquante{missingPhotoKeys.length > 1 ? 's' : ''} — revenez à
                  l’étape Photos pour téléverser les 10 clichés requis.
                </p>
              )}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-foreground">Validation</h2>
              <p className="text-sm text-muted-foreground">
                Vérifiez les pièces obligatoires avant d’envoyer la conception.
              </p>
              <ul className="space-y-3 rounded-xl border border-border/80 bg-card p-4 sm:p-5">
                <li className="flex items-center gap-2 text-sm">
                  {hasScans ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-muted-foreground shrink-0" />
                  )}
                  Scans 3D enregistrés
                </li>
                <li className="flex items-center gap-2 text-sm">
                  {hasAllRequiredPhotos ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-muted-foreground shrink-0" />
                  )}
                  10 photos cliniques complètes
                </li>
                <li className="flex items-center gap-2 text-sm">
                  {hasPackage ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-muted-foreground shrink-0" />
                  )}
                  Pack choisi
                </li>
                <li className="flex items-center gap-2 text-sm">
                  {hasSelectedServices ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-muted-foreground shrink-0" />
                  )}
                  Services optionnels
                  {hasSelectedServices
                    ? ` — ${selectedServiceIds.length} sélectionné${selectedServiceIds.length > 1 ? 's' : ''}`
                    : ' — aucun'}
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button type="button" variant="outline" onClick={() => setStep(4)} fullWidth>
                  Retour
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit || isSubmittingCase}
                  fullWidth
                >
                  {isSubmittingCase ? 'Envoi en cours…' : 'Envoyer Conception'}
                </Button>
              </div>
              {!canSubmit && (
                <p className="text-xs text-muted-foreground">
                  {!hasAllRequiredPhotos
                    ? `${missingPhotoKeys.length} photo${missingPhotoKeys.length > 1 ? 's' : ''} obligatoire${missingPhotoKeys.length > 1 ? 's' : ''} manquante${missingPhotoKeys.length > 1 ? 's' : ''} — complétez l’étape Photos avant l’envoi.`
                    : !hasPackage
                      ? 'Sélectionnez un pack à l’étape Pack et services.'
                      : phaseStatus && ['submitted', 'completed'].includes(phaseStatus)
                        ? 'Ce dossier a déjà été soumis.'
                        : 'Vérifiez que le dossier est prêt, puis réessayez.'}
                </p>
              )}
            </div>
          )}
        </div>
      </main>
      <SubmissionSuccessOverlay
        open={showSubmissionSuccess}
        onContinue={goAfterSubmitSuccess}
        redirectPath={submitSuccessRedirect}
        message="Votre soumission a bien été reçue. Nous accusons réception de votre dossier et vous recontacterons si nécessaire."
      />
    </div>
  );
};

export default CaseDentistAssistantPage;
